// src/pages/MarketMap/KpiCard.jsx
import React from "react";
import { Star } from "lucide-react";

/**
 * KPI Card with consistent vertical alignment:
 *  - Icon (left) stays vertically centered
 *  - Name (right, top) wraps up to 2 lines
 *  - Value+unit (right, near-bottom) aligned across cards, with slightly reduced gap
 *  - Adds subtle shadow for 3D effect (matches TradeTracker style)
 */
export default function KpiCard({
  displayName,
  label,
  value,
  suffix,
  Icon,
  onClick,
  isFavorite = false,
  onToggleFavorite,
  favoriteDisabled = false,
}) {
  const hasNumber = typeof value === "number" && Number.isFinite(value);
  const valueNumber = hasNumber ? Number(value) : null;
  const valueText = hasNumber ? valueNumber.toFixed(2) : "—";

  // Clamp name to two lines max
  const nameClampStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
    maxHeight: "2.6rem",
  };

  // Unit placement logic
  const rawSuffix = (suffix || "").trim();
  const isCurrencyPrefix = rawSuffix.startsWith("$");
  const currencySymbol = isCurrencyPrefix ? "$" : "";
  const suffixAfterCurrency = isCurrencyPrefix ? rawSuffix.slice(1) : "";
  const trailingUnit = isCurrencyPrefix ? suffixAfterCurrency : rawSuffix;

  return (
    <div className="card relative h-[72px] w-[174px] shrink-0 transition-shadow">
      <button
        type="button"
        onClick={onClick}
        aria-label={`Open history for ${label}`}
        className="flex h-full w-full items-center gap-2 px-2.5 py-1.5 focus:outline-none"
      >
        {/* Larger icon without a padded background box */}
        <div className="flex h-7 w-7 shrink-0 items-center justify-center">
          {Icon ? <Icon className="h-7 w-7 text-muted" /> : null}
        </div>

        {/* Right side: name at top, value pushed toward bottom with smaller gap */}
        <div className="flex h-full min-w-0 flex-1 flex-col justify-start">
          {/* Name */}
          <div
            className="pr-4 text-xs font-medium leading-tight whitespace-normal"
            title={displayName || label}
            style={nameClampStyle}
          >
            {displayName || label}
          </div>

          {/* Spacer that grows, but leaves a bit less room than justify-between */}
          <div className="mt-auto flex items-center justify-center">
            <div className="flex items-baseline gap-0">
              {currencySymbol ? (
                <span className="text-lg font-semibold tabular-nums">{currencySymbol}</span>
              ) : null}
              <span className="text-lg font-semibold tabular-nums">{valueText}</span>
              {trailingUnit ? (
                <span className="text-lg font-semibold tabular-nums">{trailingUnit}</span>
              ) : null}
            </div>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={onToggleFavorite}
        disabled={favoriteDisabled}
        aria-label={`${isFavorite ? "Remove" : "Add"} ${displayName || label} ${
          isFavorite ? "from" : "to"
        } favorites`}
        aria-pressed={isFavorite}
        className={`absolute right-1.5 top-1.5 rounded p-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-50 ${
          isFavorite ? "text-brand1" : "text-muted hover:text-brand1"
        }`}
      >
        <Star className="h-4 w-4" fill={isFavorite ? "currentColor" : "none"} />
      </button>
    </div>
  );
}
